import React, { useContext, useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Flex } from 'deprecated-enkel';
import { ClipLoader } from 'react-spinners';

import { Logo } from 'components/Logo';
import { PasswordStrength } from 'components/PasswordStrength';

import { TotemButton } from 'ui/Button';
import { InlineError } from 'ui/InlineError';

import { HandleNotification, parseQueryStrings, validateEmail, validatePhone } from 'utils';

import {
    LoginTopHalfStyle,
    LoginBottomHalfStyle,
    BorderBottomLink,
    BorderBottomText,
    LoginStyle,
} from 'styles/pages/login.styles';
import { TotemLabel, TotemInput } from 'styles';
import {
    SignupWrapperStyle,
    SignupFormStyle,
    SignupTitleStyle,
    SignupSteps,
    SignupStep,
    SignupStepActions,
    SignupOneRow,
} from 'styles/pages/signup.styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from 'contexts/AuthContext';

import { analyticsEvents, analyticsPropertiesConstants, identify, track } from 'tracking/segment';
import type { AuthContextType } from 'contexts/AuthContext';
import { GetUserDocument, GetUserQuery, useSignupMutation } from 'data/__generated__';
import { hasAccessToken } from 'data/connection/storage';

import { SignupFormValues } from './Signup.types';

// Wizard d'onboarding en une seule page. Les 3 étapes sont un état 100 % client (jamais dérivé du serveur,
// contrairement à l'ancien SignupRouter). Aucun appel API n'est fait avant la validation de la dernière
// étape : c'est la mutation `signup` unique qui crée tout le compte d'un coup (org + user + totem + Stripe +
// Odoo), ce qui supprime le doublon de customer Stripe de l'ancien flux en 3 mutations.
const STEPS: { title: string; fields: (keyof SignupFormValues)[] }[] = [
    { title: 'Compte', fields: ['email', 'password'] },
    { title: 'Entreprise', fields: ['organizationName'] },
    { title: 'Responsable', fields: ['firstName', 'lastName', 'jobTitle', 'phone'] },
];

const PASSWORD_MEDIUM_REGEX = new RegExp(
    '^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})',
);

export const Signup: React.FC = () => {
    const { search } = useLocation();
    const searchParams = parseQueryStrings(search);
    const {
        authActions: { signup, logout },
    } = useContext(AuthContext) as AuthContextType;
    const navigate = useNavigate();

    const [step, setStep] = useState(0);
    const isLastStep = step === STEPS.length - 1;

    const {
        register,
        handleSubmit,
        trigger,
        setError,
        watch,
        formState: { errors, isSubmitting },
    } = useForm<SignupFormValues>({
        // Ne pas désenregistrer les champs des étapes non affichées : leurs valeurs doivent survivre à la
        // navigation entre étapes (et à un retour à l'étape 1 en cas d'email/domaine déjà pris).
        shouldUnregister: false,
        defaultValues: { email: searchParams?.email || '' },
    });
    const dirtyPassword = watch('password', '');
    const [signupHandler] = useSignupMutation();
    const apolloClient = useApolloClient();

    useEffect(() => {
        // Un token périmé d'une session précédente ferait router App.tsx comme authentifié et casserait le
        // parcours d'inscription (non authentifié).
        if (hasAccessToken()) {
            logout();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const goToNextStep = async () => {
        const isStepValid = await trigger(STEPS[step].fields);
        if (isStepValid) {
            setStep((current) => Math.min(current + 1, STEPS.length - 1));
        }
    };

    const goToPreviousStep = () => setStep((current) => Math.max(current - 1, 0));

    const createAccount = async (values: SignupFormValues) => {
        const {
            gclid = '',
            userReferrerId = '',
            utm_term: utmTerm = '',
            utm_source: utmSource = '',
            utm_medium: utmMedium = '',
            utm_campaign: utmCampaign = '',
            utm_content: utmContent = '',
        } = searchParams;

        const parsedPhoneNumber = parsePhoneNumberFromString(values.phone ?? '', 'FR')?.number ?? '';

        try {
            const response = await signupHandler({
                variables: {
                    data: {
                        email: values.email.trim(),
                        password: values.password,
                        organizationName: values.organizationName,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        jobTitle: values.jobTitle,
                        phone: parsedPhoneNumber,
                        gclid,
                        utmTerm,
                        utmSource,
                        utmMedium,
                        utmCampaign,
                        utmContent,
                        userReferrerId,
                    },
                },
            });
            const signupResult = response?.data?.signup;

            track(analyticsEvents.SIGNUP, {
                [analyticsPropertiesConstants.SIGNUP.UTM_CAMPAIN]: utmCampaign,
                [analyticsPropertiesConstants.SIGNUP.UTM_CONTENT]: utmContent,
                [analyticsPropertiesConstants.SIGNUP.UTM_MEDIUM]: utmMedium,
                [analyticsPropertiesConstants.SIGNUP.UTM_SOURCE]: utmSource,
                [analyticsPropertiesConstants.SIGNUP.UTM_TERM]: utmTerm,
            });
            track(analyticsEvents.ADD_PERSONAL_INFO, {
                [analyticsPropertiesConstants.ONBOARDING.JOB_TITLE]: values.jobTitle,
            });

            if (signupResult?.user) {
                identify({
                    createdAt: signupResult.user.createdAt / 1000,
                    email: signupResult.user.username,
                    id: signupResult.user._id,
                });
                // Amorce le cache de GET_USER avec l'utilisateur renvoyé par la mutation (même fragment) :
                // ses `roles` sont calculés sur le document tout juste écrit, donc contiennent la permission
                // sur le site créé. Sans ça, App.tsx relit `user` dans la seconde qui suit, possiblement sur
                // un secondaire MongoDB pas encore à jour, et l'app affichait « Aucun site accessible » alors
                // que le site existait.
                apolloClient.writeQuery<GetUserQuery>({
                    query: GetUserDocument,
                    data: { user: signupResult.user },
                });
                await signup(signupResult.token!, signupResult.refreshToken!);
            }

            navigate('/');
        } catch (error: any) {
            // Email/domaine déjà pris : la création est refusée côté serveur. On ramène l'utilisateur à
            // l'étape 1 (les valeurs des autres étapes sont conservées) avec un message sur le champ email.
            const code = error?.graphQLErrors?.[0]?.extensions?.code;
            if (code === 'AHEEA' || code === 'DOMAIN_ALREADY_TAKEN') {
                setStep(0);
                setError('email', {
                    type: 'server',
                    message:
                        code === 'AHEEA'
                            ? 'Un compte existe déjà avec cet email'
                            : 'Un compte existe déjà pour ce domaine',
                });
                return;
            }
            HandleNotification(error);
        }
    };

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isLastStep) {
            await handleSubmit(createAccount)();
            return;
        }
        await goToNextStep();
    };

    return (
        <LoginStyle>
            <LoginTopHalfStyle>
                <Logo dark />
            </LoginTopHalfStyle>
            <LoginBottomHalfStyle>
                <SignupWrapperStyle $transparentize>
                    <SignupTitleStyle>
                        Créez votre espace de gestion et découvrez le catalogue produits TOTEM
                    </SignupTitleStyle>

                    <SignupSteps>
                        {STEPS.map(({ title }, index) => (
                            <SignupStep key={title} $active={index === step} $done={index < step}>
                                <span>{title}</span>
                            </SignupStep>
                        ))}
                    </SignupSteps>

                    <SignupFormStyle onSubmit={handleFormSubmit}>
                        {step === 0 && (
                            <>
                                <TotemLabel color="white">
                                    Email Professionnel <InlineError display={errors.email} />
                                    <TotemInput
                                        placeholder="Entrez votre email pro"
                                        autoFocus
                                        data-test="signup-username"
                                        {...register('email', {
                                            required: true,
                                            validate: validateEmail('Votre mail n’est pas valide'),
                                        })}
                                    />
                                </TotemLabel>
                                <TotemLabel color="white">
                                    Mot de passe <InlineError display={errors.password} />
                                    <TotemInput
                                        placeholder="Entrez votre mot de passe"
                                        type="password"
                                        autoComplete="new-password"
                                        data-test="signup-password"
                                        {...register('password', {
                                            required: true,
                                            validate: (value) =>
                                                PASSWORD_MEDIUM_REGEX.test(value) ||
                                                'Essayez un mot de passe avec une majuscule et au moins 6 caractères',
                                        })}
                                    />
                                    <PasswordStrength input={dirtyPassword} />
                                </TotemLabel>
                            </>
                        )}

                        {step === 1 && (
                            <TotemLabel color="white">
                                Nom de l'entreprise <InlineError display={errors.organizationName} />
                                <TotemInput
                                    placeholder="Nom de l'entreprise"
                                    type="text"
                                    autoFocus
                                    data-test="signup-organization-name"
                                    {...register('organizationName', { required: true })}
                                />
                            </TotemLabel>
                        )}

                        {step === 2 && (
                            <>
                                <SignupOneRow>
                                    <TotemLabel color="white">
                                        Prénom <InlineError display={errors.firstName} />
                                        <TotemInput
                                            placeholder="Entrez votre prénom"
                                            type="text"
                                            autoFocus
                                            data-test="signup-firstname"
                                            {...register('firstName', { required: true })}
                                        />
                                    </TotemLabel>
                                    <TotemLabel color="white">
                                        Nom <InlineError display={errors.lastName} />
                                        <TotemInput
                                            placeholder="Entrez votre nom"
                                            type="text"
                                            data-test="signup-lastname"
                                            {...register('lastName', { required: true })}
                                        />
                                    </TotemLabel>
                                </SignupOneRow>
                                <SignupOneRow>
                                    <TotemLabel color="white">
                                        Rôle <InlineError display={errors.jobTitle} />
                                        <TotemInput
                                            placeholder="Entrez votre rôle dans l'entreprise"
                                            type="text"
                                            data-test="signup-job-title"
                                            {...register('jobTitle', { required: true })}
                                        />
                                    </TotemLabel>
                                    <TotemLabel color="white">
                                        Numéro de téléphone <InlineError display={errors.phone} />
                                        <TotemInput
                                            placeholder="Entrez votre numéro de téléphone"
                                            type="text"
                                            data-test="signup-phone"
                                            {...register('phone', {
                                                required: true,
                                                validate: validatePhone("Votre numéro de téléphone n'est pas valide"),
                                            })}
                                        />
                                    </TotemLabel>
                                </SignupOneRow>
                            </>
                        )}

                        <Flex $alignItems="center" $direction="column">
                            <SignupStepActions>
                                {step > 0 && (
                                    <TotemButton
                                        type="button"
                                        $type="secondary"
                                        $margins={[1, 0, 1, 0]}
                                        onClick={goToPreviousStep}
                                        data-test="signup-back"
                                    >
                                        Retour
                                    </TotemButton>
                                )}
                                <TotemButton
                                    disabled={isSubmitting}
                                    $margins={[1, 0, 1, 0]}
                                    type="submit"
                                    data-test="signup-submit"
                                >
                                    {isSubmitting ? (
                                        <ClipLoader color="white" size="16px" />
                                    ) : isLastStep ? (
                                        'Créer mon compte'
                                    ) : (
                                        'Suivant'
                                    )}
                                </TotemButton>
                            </SignupStepActions>
                            <BorderBottomText>
                                Vous avez déjà un compte ?{' '}
                                <BorderBottomLink to="/login">Connectez-vous</BorderBottomLink>
                            </BorderBottomText>
                        </Flex>
                    </SignupFormStyle>
                </SignupWrapperStyle>
            </LoginBottomHalfStyle>
        </LoginStyle>
    );
};
